import { ServiceCatalogItem } from "../../../types/service-catalog";

// Service creation step types
// "env-vars" is only shown for services that require a repository (web/backend apps).
// Database and infrastructure services skip straight from "configure" to creation.
export type ServiceCreationStep = "select-type" | "configure" | "env-vars";

// Service creation data interface
export interface ServiceCreationData {
  name: string;
  repository: {
    url: string;
    branch: string;
    rootDirectory?: string;
    git_account_id?: string;
  };
  framework: string;
  buildCommand: string;
  startCommand: string;
  port: string;
  envVars: Record<string, string>;
  databaseName: string;
  databaseUser: string;
  databasePassword: string;
  queueName: string;
  schedule: string;
  description: string;
  customCredentials?: Record<string, string>; // NEW: Custom credentials from catalog
}

// Service category options
export const SERVICE_CATEGORIES = [
  { value: "all", label: "All Categories" },
  { value: "web-applications", label: "Web Applications" },
  { value: "databases", label: "Databases" },
  { value: "development", label: "Development" },
  { value: "ai-ml", label: "AI/ML" },
  { value: "collaboration", label: "Collaboration" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "devops", label: "DevOps" },
  { value: "analytics", label: "Analytics" },
  { value: "security", label: "Security" },
];

// Legacy service type options for fallback
export const FALLBACK_SERVICE_TYPES = [
  {
    id: "web-frontend",
    name: "Web Frontend",
    description: "React, Vue, Angular applications",
    icon: "Globe",
    category: "Web Applications",
    type: "web-frontend",
    tags: ["frontend", "react", "vue", "angular"],
    isPopular: true,
  },
  {
    id: "web-backend",
    name: "Web Backend",
    description: "Express, FastAPI, Node.js APIs",
    icon: "Server",
    category: "Web Applications",
    type: "web-backend",
    tags: ["backend", "api", "node", "express"],
    isPopular: true,
  },
  {
    id: "static",
    name: "Static Site",
    description: "Jekyll, Hugo, static files",
    icon: "Code",
    category: "Web Applications",
    type: "static",
    tags: ["static", "jekyll", "hugo"],
    isPopular: false,
  },
  {
    id: "database-relational",
    name: "PostgreSQL",
    description: "PostgreSQL database",
    icon: "Database",
    category: "Databases",
    type: "database-relational",
    tags: ["database", "postgres", "sql"],
    isPopular: true,
  },
];

// Default service creation data
export const DEFAULT_SERVICE_DATA: ServiceCreationData = {
  name: "",
  repository: { url: "", branch: "main" },
  framework: "",
  buildCommand: "",
  startCommand: "",
  port: "",
  envVars: {},
  databaseName: "",
  databaseUser: "",
  databasePassword: "",
  queueName: "",
  schedule: "",
  description: "",
};

// Tab types for service selection
export type ServiceSelectionTab = "browse" | "popular" | "search";

// Props interfaces for components
export interface ServiceTemplateCardProps {
  template: ServiceCatalogItem | any;
  onSelect: (template: ServiceCatalogItem | any) => void;
}

export interface ServiceSearchControlsProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}

export interface ServiceTemplateSelectorProps {
  serviceCatalog: Record<string, ServiceCatalogItem[]>;
  serviceCatalogLoading: boolean;
  searchResults: ServiceCatalogItem[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onTemplateSelect: (template: ServiceCatalogItem | any) => void;
  selectedTemplate?: ServiceCatalogItem | null;
}

export interface ServiceConfigurationFormProps {
  selectedTemplate: ServiceCatalogItem | null;
  serviceData: ServiceCreationData;
  onServiceDataChange: (data: ServiceCreationData) => void;
  onBack: () => void;
  /** If provided, the footer CTA advances to the next step instead of deploying. */
  onContinue?: () => void;
  onCreate: () => void;
  isCreating: boolean;
  /** When true, hides the service icon header and footer buttons.
   *  The parent page owns those when using the two-column wizard layout. */
  embedded?: boolean;
}
